/**
 * ArchitectOnboardingWizard
 *
 * Specialized onboarding for Enterprise Architects.
 * Steps:
 * 1. Import Target - Import the repository/system you're evaluating for acquisition
 * 2. Import Own - Import your own system for compatibility analysis
 * 3. Define Requirements - Specify integration requirements, constraints, architecture preferences
 * 4. Run Analysis - Summary and trigger comparative analysis
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Building2, GitBranch, Settings, Play, Check, ChevronRight, ChevronLeft,
  X, Shield, Layers, Database, Cloud, Server, Cpu,
} from 'lucide-react';
import Button from '../ui/Button';

const STEPS = [
  { id: 'import-target', title: 'Target', icon: GitBranch },
  { id: 'import-own', title: 'Your System', icon: Building2 },
  { id: 'requirements', title: 'Requirements', icon: Settings },
  { id: 'analyze', title: 'Analyze', icon: Play },
];

export const ARCHITECTURE_PATTERNS = [
  { id: 'microservices', label: 'Microservices', icon: Layers },
  { id: 'monolith', label: 'Monolithic', icon: Server },
  { id: 'serverless', label: 'Serverless', icon: Cloud },
  { id: 'event-driven', label: 'Event-Driven', icon: Cpu },
  { id: 'hybrid', label: 'Hybrid', icon: Database },
];

export const INTEGRATION_CONSTRAINTS = [
  { id: 'data-migration', label: 'Data Migration Required' },
  { id: 'api-compat', label: 'API Compatibility' },
  { id: 'auth-integration', label: 'Auth Integration' },
  { id: 'ci-cd-merge', label: 'CI/CD Pipeline Merge' },
  { id: 'infra-consolidation', label: 'Infrastructure Consolidation' },
];

/** Progress stepper matching OnboardingWizard style */
const ProgressStepper = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, index) => {
      const isCompleted = index < currentStep;
      const isCurrent = index === currentStep;
      const Icon = step.icon;
      return (
        <React.Fragment key={step.id}>
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              isCompleted
                ? 'bg-success-500 border-success-500'
                : isCurrent
                ? 'bg-primary-500/20 border-primary-500'
                : 'bg-white/5 border-white/20'
            }`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            {isCompleted ? (
              <Check className="w-5 h-5 text-white" />
            ) : (
              <Icon className={`w-5 h-5 ${isCurrent ? 'text-primary-400' : 'text-gray-500'}`} />
            )}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 h-0.5 transition-colors duration-300 ${
              isCompleted ? 'bg-success-500' : 'bg-white/20'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

ProgressStepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
    })
  ).isRequired,
};

/** Step 1: Import Target */
const ImportTargetStep = ({ targetRepo, onImportTarget, onNext, onSkip }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-secondary-500/20 mb-4">
        <GitBranch className="w-12 h-12 text-secondary-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Import Acquisition Target</h2>
      <p className="text-gray-400 max-w-md mx-auto">
        Import the repository or system you are evaluating for acquisition or integration.
      </p>
    </div>

    <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-xl p-5 mb-6 text-left max-w-md mx-auto space-y-3">
      <div className="flex items-start gap-3">
        <GitBranch className="w-5 h-5 text-secondary-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Connect target repository</div>
          <div className="text-xs text-gray-500">Import the codebase you want to evaluate</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-success-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Analysis only</div>
          <div className="text-xs text-gray-500">We analyze patterns and architecture, never store source code</div>
        </div>
      </div>
    </div>

    {targetRepo ? (
      <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-success-500/20 text-success-400 rounded-full">
        <Check className="w-5 h-5" />
        <span className="text-sm font-medium">{targetRepo.name || 'Target imported'}</span>
      </div>
    ) : (
      <Button variant="gradient" size="lg" onClick={onImportTarget} icon={GitBranch} className="mb-6">
        Import Target Repository
      </Button>
    )}

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button
        variant="gradient"
        size="md"
        onClick={onNext}
        disabled={!targetRepo}
        icon={ChevronRight}
        iconPosition="right"
      >
        Continue
      </Button>
      <Button variant="ghost" size="md" onClick={onSkip}>
        Skip for now
      </Button>
    </div>
  </div>
);

ImportTargetStep.propTypes = {
  targetRepo: PropTypes.object,
  onImportTarget: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

/** Step 2: Import Own System */
const ImportOwnStep = ({ ownRepo, onImportOwn, onNext, onBack }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-primary-500/20 mb-4">
        <Building2 className="w-12 h-12 text-primary-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Import Your System</h2>
      <p className="text-gray-400 max-w-md mx-auto">
        Import your own codebase so we can analyze compatibility and integration effort.
      </p>
    </div>

    <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-xl p-5 mb-6 text-left max-w-md mx-auto space-y-3">
      <div className="flex items-start gap-3">
        <Building2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Your existing system</div>
          <div className="text-xs text-gray-500">We compare architecture, tech stack, and patterns</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Layers className="w-5 h-5 text-warning-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Compatibility analysis</div>
          <div className="text-xs text-gray-500">Identify overlaps, gaps, and integration friction</div>
        </div>
      </div>
    </div>

    {ownRepo ? (
      <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-success-500/20 text-success-400 rounded-full">
        <Check className="w-5 h-5" />
        <span className="text-sm font-medium">{ownRepo.name || 'System imported'}</span>
      </div>
    ) : (
      <Button variant="gradient" size="lg" onClick={onImportOwn} icon={Building2} className="mb-6">
        Import Your Repository
      </Button>
    )}

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>Back</Button>
      <Button
        variant="gradient"
        size="md"
        onClick={onNext}
        icon={ChevronRight}
        iconPosition="right"
      >
        Continue
      </Button>
    </div>
  </div>
);

ImportOwnStep.propTypes = {
  ownRepo: PropTypes.object,
  onImportOwn: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

/** Step 3: Define Requirements */
const RequirementsStep = ({
  architecturePreference,
  setArchitecturePreference,
  constraints,
  toggleConstraint,
  notes,
  setNotes,
  onNext,
  onBack,
}) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-warning-500/20 mb-4">
        <Settings className="w-12 h-12 text-warning-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Define Requirements</h2>
      <p className="text-gray-400 max-w-md mx-auto">
        Specify your architecture preferences and integration constraints.
      </p>
    </div>

    <div className="max-w-md mx-auto mb-6 text-left">
      <label className="block text-sm font-medium text-gray-300 mb-3">
        Preferred Architecture Pattern
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
        {ARCHITECTURE_PATTERNS.map((pattern) => {
          const Icon = pattern.icon;
          const isSelected = architecturePreference === pattern.id;
          return (
            <button
              key={pattern.id}
              onClick={() => setArchitecturePreference(pattern.id)}
              className={`p-3 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-white/10 bg-white/[0.03] hover:border-white/20'
              }`}
              aria-pressed={isSelected}
            >
              <Icon className={`w-5 h-5 mb-1.5 ${isSelected ? 'text-primary-400' : 'text-gray-400'}`} />
              <div className="text-xs font-medium text-white">{pattern.label}</div>
            </button>
          );
        })}
      </div>

      <label className="block text-sm font-medium text-gray-300 mb-3">
        Integration Constraints
      </label>
      <div className="space-y-2 mb-6">
        {INTEGRATION_CONSTRAINTS.map((constraint) => {
          const isSelected = constraints.includes(constraint.id);
          return (
            <button
              key={constraint.id}
              onClick={() => toggleConstraint(constraint.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-white/10 bg-white/[0.03] hover:border-white/20'
              }`}
              aria-pressed={isSelected}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'border-primary-500 bg-primary-500' : 'border-white/20'
              }`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="text-sm text-white">{constraint.label}</span>
            </button>
          );
        })}
      </div>

      <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="architect-notes">
        Additional Notes
      </label>
      <textarea
        id="architect-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Any specific requirements or concerns..."
        rows={3}
        className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
      />
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>Back</Button>
      <Button variant="gradient" size="md" onClick={onNext} icon={ChevronRight} iconPosition="right">
        Continue
      </Button>
    </div>
  </div>
);

RequirementsStep.propTypes = {
  architecturePreference: PropTypes.string,
  setArchitecturePreference: PropTypes.func.isRequired,
  constraints: PropTypes.arrayOf(PropTypes.string).isRequired,
  toggleConstraint: PropTypes.func.isRequired,
  notes: PropTypes.string.isRequired,
  setNotes: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

/** Step 4: Run Analysis */
const AnalyzeStep = ({ targetRepo, ownRepo, architecturePreference, constraints, onComplete }) => {
  const archPattern = ARCHITECTURE_PATTERNS.find(p => p.id === architecturePreference);

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-success-500/20 mb-4">
          <Play className="w-12 h-12 text-success-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Ready to Analyze</h2>
        <p className="text-gray-400 max-w-md mx-auto">
          We will run a comparative analysis between the target and your system.
        </p>
      </div>

      <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-xl p-5 mb-6 max-w-md mx-auto text-left space-y-3">
        {targetRepo && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Target:</span>
            <span className="text-white font-medium">{targetRepo.name || 'Selected'}</span>
          </div>
        )}
        {ownRepo && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Your System:</span>
            <span className="text-white font-medium">{ownRepo.name || 'Selected'}</span>
          </div>
        )}
        {archPattern && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Architecture:</span>
            <span className="text-white font-medium">{archPattern.label}</span>
          </div>
        )}
        {constraints.length > 0 && (
          <div className="pt-2 border-t border-white/10">
            <div className="text-xs text-gray-500 mb-2">Constraints:</div>
            <div className="flex flex-wrap gap-1.5">
              {constraints.map((id) => {
                const c = INTEGRATION_CONSTRAINTS.find(ic => ic.id === id);
                return c ? (
                  <span key={id} className="inline-block px-2 py-0.5 text-xs rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30">
                    {c.label}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-sm text-gray-500">
          Next: Run the comparative analysis and view the integration assessment.
        </p>
        <Button variant="gradient" size="lg" onClick={onComplete} icon={Play}>
          Run Comparative Analysis
        </Button>
      </div>
    </div>
  );
};

AnalyzeStep.propTypes = {
  targetRepo: PropTypes.object,
  ownRepo: PropTypes.object,
  architecturePreference: PropTypes.string,
  constraints: PropTypes.arrayOf(PropTypes.string).isRequired,
  onComplete: PropTypes.func.isRequired,
};

/**
 * Main ArchitectOnboardingWizard Component
 */
const ArchitectOnboardingWizard = ({ onComplete, onClose, onImportTarget, onImportOwn, initialData }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState(() => {
    const defaults = {
      targetRepo: null,
      ownRepo: null,
      architecturePreference: '',
      constraints: [],
      notes: '',
    };
    return initialData ? { ...defaults, ...initialData } : defaults;
  });

  const saveProgress = useCallback((updates) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
  }, []);

  const toggleConstraint = useCallback((constraintId) => {
    setWizardData((prev) => {
      const exists = prev.constraints.includes(constraintId);
      return {
        ...prev,
        constraints: exists
          ? prev.constraints.filter(c => c !== constraintId)
          : [...prev.constraints, constraintId],
      };
    });
  }, []);

  const handleComplete = () => {
    if (onComplete) {
      onComplete(wizardData);
    }
  };

  const handleImportTarget = () => {
    if (onImportTarget) {
      onImportTarget();
    }
  };

  const handleImportOwn = () => {
    if (onImportOwn) {
      onImportOwn();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" aria-hidden="true" />
      <div className="relative bg-gray-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors z-10"
          aria-label="Close architect setup"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8">
          <ProgressStepper currentStep={currentStep} steps={STEPS} />

          <div className="min-h-[300px]">
            {currentStep === 0 && (
              <ImportTargetStep
                targetRepo={wizardData.targetRepo}
                onImportTarget={handleImportTarget}
                onNext={() => setCurrentStep(1)}
                onSkip={onClose}
              />
            )}
            {currentStep === 1 && (
              <ImportOwnStep
                ownRepo={wizardData.ownRepo}
                onImportOwn={handleImportOwn}
                onNext={() => setCurrentStep(2)}
                onBack={() => setCurrentStep(0)}
              />
            )}
            {currentStep === 2 && (
              <RequirementsStep
                architecturePreference={wizardData.architecturePreference}
                setArchitecturePreference={(v) => saveProgress({ architecturePreference: v })}
                constraints={wizardData.constraints}
                toggleConstraint={toggleConstraint}
                notes={wizardData.notes}
                setNotes={(v) => saveProgress({ notes: v })}
                onNext={() => setCurrentStep(3)}
                onBack={() => setCurrentStep(1)}
              />
            )}
            {currentStep === 3 && (
              <AnalyzeStep
                targetRepo={wizardData.targetRepo}
                ownRepo={wizardData.ownRepo}
                architecturePreference={wizardData.architecturePreference}
                constraints={wizardData.constraints}
                onComplete={handleComplete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ArchitectOnboardingWizard.propTypes = {
  /** Callback when wizard completes with collected data */
  onComplete: PropTypes.func.isRequired,
  /** Callback to close the wizard */
  onClose: PropTypes.func.isRequired,
  /** Callback to trigger target repo import */
  onImportTarget: PropTypes.func,
  /** Callback to trigger own repo import */
  onImportOwn: PropTypes.func,
  /** Initial data to pre-populate the wizard */
  initialData: PropTypes.shape({
    targetRepo: PropTypes.object,
    ownRepo: PropTypes.object,
    architecturePreference: PropTypes.string,
    constraints: PropTypes.arrayOf(PropTypes.string),
    notes: PropTypes.string,
  }),
};

export default ArchitectOnboardingWizard;
